/**
 * 页数字段验证和格式化工具函数
 * 统一处理场次表和场次详情中的页数字段输入验证
 */

/**
 * 验证页数输入是否合法（实时输入验证，用于 onChange）
 * 规则：
 * - 只能输入数字和小数点
 * - 范围：0-99.9
 * - 最多1位小数
 * - 整数部分最多2位数字
 * 
 * @param input 用户输入的字符串
 * @returns 是否允许该输入
 */
export const validatePageNumberInput = (input: string): boolean => {
  // 1. 允许空值
  if (input === '') return true;
  
  // 2. 将全角句号转换为半角句号（处理中文输入法下的句号）
  const normalizedInput = input.replace(/。/g, '.');
  
  // 3. 只允许数字和小数点
  if (!/^[\d.]*$/.test(normalizedInput)) return false;
  
  // 4. 最多一个小数点
  const dotCount = (normalizedInput.match(/\./g) || []).length;
  if (dotCount > 1) return false;
  
  // 5. 如果已有小数点，检查小数位数
  const parts = normalizedInput.split('.');
  if (parts.length === 2) {
    // 小数部分最多1位
    if (parts[1].length > 1) return false;
  }
  
  // 6. 检查整数部分长度（最多2位数字）
  const integerPart = parts[0];
  if (integerPart.length > 2) return false;
  
  // 7. 如果输入完整（不以小数点结尾），检查范围（0-99.9）
  if (!normalizedInput.endsWith('.') && normalizedInput !== '') {
    const numValue = parseFloat(normalizedInput);
    if (!isNaN(numValue)) {
      // 范围检查：0-99.9（允许0开头，如0.5）
      if (numValue < 0 || numValue > 99.9) return false;
    }
  }
  
  return true;
};

/**
 * 验证并格式化页数值（用于 onBlur 或提交时）
 * 规则：
 * - 范围：0-99.9
 * - 最多1位小数
 * - 自动四舍五入到1位小数
 * 
 * @param input 用户输入的字符串
 * @returns 验证后的数字值，无效则返回 undefined
 */
export const validateAndFormatPageNumber = (
  input: string
): number | undefined => {
  const trimmed = input.trim();
  
  // 允许空值
  if (trimmed === '') {
    return undefined;
  }
  
  const numValue = parseFloat(trimmed);
  if (isNaN(numValue)) {
    return undefined;
  }
  
  // 检查范围：0-99.9（允许0开头，如0.5）
  if (numValue < 0 || numValue > 99.9) {
    return undefined;
  }
  
  // 检查小数位数（最多一位）
  const decimalParts = trimmed.split('.');
  if (decimalParts.length === 2 && decimalParts[1].length > 1) {
    return undefined;
  }
  
  // 四舍五入到1位小数
  const rounded = Math.round(numValue * 10) / 10;
  
  // 再次检查范围（防止四舍五入后超出范围）
  if (rounded < 0 || rounded > 99.9) {
    return undefined;
  }
  
  return rounded;
};

/**
 * 格式化页数显示（统一显示1位小数）
 * 例如：1.0 -> "1.0", 1.5 -> "1.5"
 * 
 * @param value 数字值
 * @returns 格式化后的字符串
 */
export const formatPageNumberDisplay = (
  value: number | null | undefined
): string => {
  if (value === null || value === undefined) return '';
  
  // 四舍五入到1位小数
  const rounded = Math.round(value * 10) / 10;
  
  // 统一显示1位小数
  return rounded.toFixed(1);
};




